import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, inr } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { Storefront, Package, ArrowsClockwise, Users, TrendUp } from "@phosphor-icons/react";

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</span>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent || "bg-slate-100 text-slate-700"}`}>
          <Icon size={16} weight="duotone" />
        </div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-slate-900 font-mono-num tracking-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Overview() {
  const { user, stores } = useAuth();
  const nav = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [skuCount, setSkuCount] = useState(0);
  const [lastSync, setLastSync] = useState("");

  useEffect(() => {
    api.get("/warehouse/skus?limit=1").then((r) => setSkuCount(r.data.total || 0)).catch(() => {});
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/warehouse/sync-skus");
      setLastSync(new Date().toLocaleString());
      const r = await api.get("/warehouse/skus?limit=1");
      setSkuCount(r.data.total || 0);
      alert(`Synced ${data.synced} SKUs from Main Warehouse.`);
    } catch (e) {
      alert("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const isAdmin = user?.role === "super_admin" || user?.role === "warehouse_manager";

  return (
    <Shell>
      <PageHeader
        title={`Welcome, ${user?.name?.split(' ')[0] || 'there'}`}
        subtitle="Your retail operations at a glance."
        testId="overview-header"
        actions={isAdmin ? (
          <button
            onClick={sync}
            disabled={syncing}
            data-testid="sync-skus-btn"
            className="inline-flex items-center gap-2 h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-md transition-colors disabled:opacity-60"
          >
            <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing…" : "Sync Warehouse SKUs"}
          </button>
        ) : null}
      />

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Storefront} label="Stores" value={stores.length} sub="Active locations" accent="bg-emerald-50 text-emerald-700" />
          <Stat icon={Package} label="Warehouse SKUs" value={skuCount.toLocaleString("en-IN")} sub={lastSync ? `Synced ${lastSync}` : "Click sync to pull latest"} accent="bg-amber-50 text-amber-700" />
          <Stat icon={TrendUp} label="Role" value={(user?.role || "").replace("_"," ")} accent="bg-slate-900 text-white" />
          <Stat icon={Users} label="Permissions" value={user?.permissions?.length || 0} sub={user?.permissions?.includes("*") ? "Full access" : "Custom scope"} accent="bg-slate-100 text-slate-700" />
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="font-display text-xl font-bold text-slate-900">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            {isAdmin && (
              <>
                <button onClick={() => nav("/warehouse/skus")} data-testid="qa-skus" className="text-left p-4 border border-slate-200 rounded-md hover:border-slate-900 transition-colors">
                  <Package size={20} weight="duotone" className="text-slate-700" />
                  <div className="mt-2 font-semibold text-sm">SKU Master</div>
                  <div className="text-xs text-slate-500 mt-0.5">Edit category, HSN, GST, pricing</div>
                </button>
                <button onClick={() => nav("/warehouse/dispatches")} data-testid="qa-dispatch" className="text-left p-4 border border-slate-200 rounded-md hover:border-slate-900 transition-colors">
                  <ArrowsClockwise size={20} weight="duotone" className="text-slate-700" />
                  <div className="mt-2 font-semibold text-sm">Create Dispatch</div>
                  <div className="text-xs text-slate-500 mt-0.5">Send inventory to a store with GST invoice</div>
                </button>
                <button onClick={() => nav("/stores")} data-testid="qa-stores" className="text-left p-4 border border-slate-200 rounded-md hover:border-slate-900 transition-colors">
                  <Storefront size={20} weight="duotone" className="text-slate-700" />
                  <div className="mt-2 font-semibold text-sm">Manage Stores</div>
                  <div className="text-xs text-slate-500 mt-0.5">Add or update stores</div>
                </button>
              </>
            )}
          </div>
        </div>

        {stores.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="font-display text-xl font-bold">Your Stores</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {stores.map((s) => (
                <button
                  key={s.id}
                  data-testid={`store-link-${s.code}`}
                  onClick={() => nav(`/store/${s.id}`)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div>
                    <div className="font-semibold text-sm text-slate-900">{s.name}</div>
                    <div className="text-xs text-slate-500 font-mono-num mt-0.5">{s.code} · {s.state} {s.state_code ? `(${s.state_code})` : ""}</div>
                  </div>
                  <span className="text-xs text-emerald-600 font-semibold">Open →</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
